// src/pages/league/DraftPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DraftStateResponse, DraftPlayerInfo, League, Team } from '../../types';
import BackLink from '../../components/common/BackLink';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { useDraftPolling } from '../../hooks/useDraftPolling';
import { seasonAverage } from '../../utils/playerSeason';

export default function DraftPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user } = useAuth();

  const [draftState, setDraftState] = useState<DraftStateResponse | null>(null);
  const [league, setLeague] = useState<League | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<DraftPlayerInfo | null>(null);
  const [isDrafting, setIsDrafting] = useState(false);
  const [pickError, setPickError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);

  useEffect(() => {
    if (!leagueId) return;

    // TODO: Reduce to a single getDraftState call once the backend extends
    // DraftStateResponse to include league_name and user_id per team.
    // Follow-up task — not blocking this session.
    Promise.all([
      apiService.getDraftState(Number(leagueId)),
      apiService.getLeague(Number(leagueId)),
      apiService.getTeams(Number(leagueId)),
    ])
      .then(([state, leagueData, teamsData]) => {
        setDraftState(state);
        setLeague(leagueData);
        setTeams(teamsData);
      })
      .catch(err => setError(err.message || 'Failed to load draft'))
      .finally(() => setLoading(false));
  }, [leagueId]);

  // Keep every screen in the room current. Without this only the person
  // whose turn it was ever saw anything change.
  useDraftPolling(leagueId ? Number(leagueId) : undefined, draftState?.is_complete, (next) => {
    setDraftState(next);
    setLastSync(Date.now());
  });

  const myTeam = teams.find(t => t.user_id === user?.id);
  const isMyTurn = !!myTeam && myTeam.id === draftState?.current_team_id;
  const isCommissioner = user?.id === league?.commissioner_id;

  // draft_order is round-1 only; compute the full snake sequence on the frontend
  const fullDraftOrder = useMemo(() => {
    if (!draftState) return [];
    const { draft_order, total_picks, teams: draftTeams } = draftState;
    const numTeams = draft_order.length;
    if (!numTeams) return [];
    const nameMap: Record<number, string> = {};
    draftTeams.forEach(t => { nameMap[t.team_id] = t.team_name; });
    const totalRounds = Math.ceil(total_picks / numTeams);
    const result: Array<{ pick: number; team_id: number; team_name: string }> = [];
    for (let round = 1; round <= totalRounds; round++) {
      const order = round % 2 === 1 ? draft_order : [...draft_order].reverse();
      order.forEach(teamId => {
        result.push({ pick: result.length + 1, team_id: teamId, team_name: nameMap[teamId] ?? '' });
      });
    }
    return result;
  }, [draftState]);

  const handleDraftPlayer = async (playerId: number) => {
    if (!myTeam || !leagueId) return;
    setIsDrafting(true);
    setPickError(null);
    try {
      const response = await apiService.makePick(Number(leagueId), myTeam.id, playerId);
      setDraftState(response.state);
      setSelectedPlayer(null);
    } catch (err: any) {
      setPickError(err.message || 'Failed to make pick');
    } finally {
      setIsDrafting(false);
    }
  };

  const handleAutoPick = async () => {
    if (!leagueId || !draftState?.current_team_id) return;
    setIsDrafting(true);
    setPickError(null);
    try {
      const response = await apiService.autoPick(Number(leagueId), draftState.current_team_id);
      setDraftState(response.state);
      setSelectedPlayer(null);
    } catch (err: any) {
      setPickError(err.message || 'Auto-pick failed');
    } finally {
      setIsDrafting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="loading-spinner w-8 h-8 mr-3" />
        <span className="text-secondary-600">Loading draft...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <p className="text-danger-600">{error}</p>
        <BackLink to={`/app/leagues/${leagueId}`}>Back to League</BackLink>
      </div>
    );
  }

  if (!draftState) return null;

  if (draftState.status === 'setup') {
    return (
      <div className="space-y-4">
        <p className="text-secondary-600">Draft hasn't started yet.</p>
        <BackLink to={`/app/leagues/${leagueId}`}>Back to League</BackLink>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Draft complete banner */}
      {draftState.is_complete && (
        <div className="bg-secondary-100 border border-secondary-300 px-5 py-4 flex items-center justify-between">
          <div>
            <p className="font-display uppercase tracking-wide text-ink-900 text-xl leading-none">Draft complete</p>
            {league?.name && <p className="text-sm text-secondary-600 mt-1">{league.name}</p>}
          </div>
          <Link to={`/app/leagues/${leagueId}`} className="btn-primary text-sm">
            Back to League
          </Link>
        </div>
      )}

      {/* Header */}
      <BackLink to={`/app/leagues/${leagueId}`}>Back to league</BackLink>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between border-b-2 border-ink-900 pb-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl uppercase tracking-tight text-ink-900">Draft</h1>
          {league?.name && <p className="text-sm text-secondary-500 mt-1">{league.name}</p>}
        </div>
        <span className="inline-flex items-center self-start sm:self-auto shrink-0 px-3 py-1 text-sm font-medium bg-secondary-100 text-ink-900 whitespace-nowrap">
          {draftState.is_complete
            ? 'Draft Complete'
            : `Pick ${draftState.current_pick} of ${draftState.total_picks} · Round ${draftState.current_round}`}
        </span>
      </div>

      {/* Whose-turn banner */}
      {!draftState.is_complete && (
        isMyTurn ? (
          <div className="bg-success-50 border border-success-200 rounded-none px-4 py-3 flex items-center space-x-3">
            <span className="font-display uppercase tracking-wide text-success-800 text-xl leading-none">You're up</span>
            <span className="text-success-700 text-sm">— {myTeam?.team_name}</span>
          </div>
        ) : draftState.current_team ? (
          <div className="border-l-2 border-ink-900 pl-3 py-1">
            <p className="text-sm text-secondary-600">
              On the clock: <span className="font-semibold text-ink-900">{draftState.current_team.team_name}</span>
            </p>
            <p className="text-xs text-secondary-400 mt-0.5">
              This board updates on its own — no need to refresh.
            </p>
          </div>
        ) : null
      )}

      {/* Live indicator. After a frozen board sent three separate leagues to
          free agency instead, people need to SEE that it is refreshing. */}
      {!draftState.is_complete && (
        <p className="text-xs uppercase tracking-wider text-secondary-400 flex items-center gap-1.5"
           data-testid="draft-live-indicator">
          <span className="inline-block w-1.5 h-1.5 bg-success-600" aria-hidden="true" />
          Live
          {lastSync && <span className="text-secondary-300">· synced {new Date(lastSync).toLocaleTimeString()}</span>}
        </p>
      )}

      {/* Auto-pick button */}
      {!draftState.is_complete && (isMyTurn || isCommissioner) && (
        <div className="flex items-center space-x-3">
          <button
            onClick={handleAutoPick}
            disabled={isDrafting}
            className="btn-outline-secondary text-sm flex items-center"
          >
            {isDrafting ? (
              <>
                <div className="loading-spinner w-4 h-4 mr-2" />
                Auto-picking...
              </>
            ) : (
              'Auto-pick'
            )}
          </button>
          {!isMyTurn && isCommissioner && (
            <span className="text-xs text-secondary-500">Commissioner override</span>
          )}
        </div>
      )}

      {/* Pick error banner (when modal is not open) */}
      {pickError && !selectedPlayer && (
        <div className="bg-danger-50 border border-danger-200 rounded-none px-4 py-3 flex items-center justify-between">
          <p className="text-danger-700 text-sm">{pickError}</p>
          <button onClick={() => setPickError(null)} className="text-danger-400 hover:text-danger-600 ml-3">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Available Players — spans 2 cols */}
        <div className="lg:col-span-2 card">
          <div className="card-header">
            <h2 className="font-display uppercase text-lg tracking-wide text-ink-900">
              Available Players ({draftState.available_players.length} remaining)
            </h2>
          </div>
          <div className="divide-y divide-secondary-100 max-h-[600px] overflow-y-auto">
            {draftState.available_players.length === 0 ? (
              <p className="px-6 py-4 text-secondary-500 text-sm">No available players.</p>
            ) : (
              draftState.available_players.map(player => {
                const season = seasonAverage(player);
                return (
                <div
                  key={player.id}
                  onClick={() => { setSelectedPlayer(player); setPickError(null); }}
                  className="flex items-center justify-between px-6 py-3 hover:bg-secondary-50 cursor-pointer"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium shrink-0 ${
                      player.position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800'
                    }`}>
                      {player.position}
                    </span>
                    <span className="font-medium text-secondary-900 truncate">{player.name}</span>
                    {player.nfl_team_abbr && (
                      <span className="text-xs text-secondary-500 shrink-0">{player.nfl_team_abbr}</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-3 shrink-0 ml-4">
                    <span className="text-right shrink-0">
                      <span className="block text-sm text-secondary-600 tabular-nums">
                        {season.display}
                      </span>
                      <span className="block text-[0.6rem] uppercase tracking-wider text-secondary-500">
                        {season.label}
                      </span>
                    </span>
                    {!draftState.is_complete && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDraftPlayer(player.id); }}
                        disabled={!isMyTurn || isDrafting}
                        className={`icon-btn text-xs px-3 py-1 font-medium transition-colors ${
                          isMyTurn && !isDrafting
                            ? 'bg-ink-900 text-paper hover:bg-ink-800'
                            : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                        }`}
                      >
                        Draft
                      </button>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* On the Clock */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-display uppercase text-base tracking-wide text-ink-900">On the Clock</h2>
            </div>
            <div className="card-body space-y-1">
              {draftState.is_complete ? (
                <p className="text-secondary-500 text-sm">Draft complete.</p>
              ) : (
                <>
                  <p className="text-xl font-bold text-secondary-900">
                    {draftState.current_team?.team_name ?? '—'}
                  </p>
                  <p className="text-sm text-secondary-600">
                    Round {draftState.current_round} · Pick {draftState.current_pick} of {draftState.total_picks}
                  </p>
                  <p className="text-sm text-secondary-500">
                    Kickers: {draftState.current_team?.kicker_count ?? 0}/3 · Punters: {draftState.current_team?.punter_count ?? 0}/3
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Draft Order */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-display uppercase text-base tracking-wide text-ink-900">Draft Order</h2>
            </div>
            <div className="divide-y divide-secondary-100 max-h-64 overflow-y-auto">
              {fullDraftOrder.map(({ pick, team_name }) => {
                const isCurrent = pick === draftState.current_pick && !draftState.is_complete;
                const isPast = pick < draftState.current_pick;
                return (
                  <div
                    key={pick}
                    data-testid={isCurrent ? 'draft-order-current' : undefined}
                    className={`flex items-center space-x-3 px-4 py-2 ${
                      isCurrent ? 'bg-gold/10 border-l-2 border-gold pl-3.5' : 'border-l-2 border-transparent pl-3.5'
                    }`}
                  >
                    <span className={`text-xs w-6 text-right shrink-0 tabular-nums ${
                      isPast ? 'text-secondary-300' : isCurrent ? 'font-bold text-ink-900' : 'text-secondary-400'
                    }`}>
                      {pick}
                    </span>
                    <span className={`text-sm truncate ${
                      isPast ? 'text-secondary-400' : isCurrent ? 'font-semibold text-ink-900' : 'text-secondary-700'
                    }`}>
                      {team_name}
                    </span>
                    {isCurrent && (
                      <span className="ml-auto text-[0.65rem] font-semibold uppercase tracking-wider text-gold-700 shrink-0">
                        on the clock
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Picks */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-display uppercase text-base tracking-wide text-ink-900">Recent Picks</h2>
            </div>
            <div className="divide-y divide-secondary-100">
              {draftState.recent_picks.length === 0 ? (
                <p className="px-4 py-3 text-secondary-500 text-sm">No picks yet.</p>
              ) : (
                draftState.recent_picks.map(pick => (
                  <div key={pick.pick_number} className="flex items-center space-x-2 px-4 py-2">
                    <span className="text-xs text-secondary-400 w-5 text-right shrink-0 tabular-nums">
                      #{pick.pick_number}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium shrink-0 ${
                      pick.position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800'
                    }`}>
                      {pick.position}
                    </span>
                    <span className="text-sm font-medium text-secondary-900 truncate">{pick.player_name}</span>
                    <span className="text-xs text-secondary-500 shrink-0 ml-auto">{pick.team_name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Player detail modal */}
      {selectedPlayer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { setSelectedPlayer(null); setPickError(null); }}
        >
          <div
            className="bg-white rounded-none max-w-sm w-full shadow-xl"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2.5 py-1 text-sm font-medium ${
                  selectedPlayer.position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800'
                }`}>
                  {selectedPlayer.position === 'K' ? 'Kicker' : 'Punter'}
                </span>
                {selectedPlayer.nfl_team_abbr && (
                  <span className="text-sm text-secondary-600">{selectedPlayer.nfl_team_abbr}</span>
                )}
              </div>
              <button
                onClick={() => { setSelectedPlayer(null); setPickError(null); }}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5">
              <h2 className="font-display text-2xl uppercase tracking-tight text-ink-900 mb-4">{selectedPlayer.name}</h2>
              <div className="grid grid-cols-2 gap-4 mb-2">
                <div className="bg-secondary-50 rounded-none p-4 text-center">
                  <div className="font-display text-3xl text-ink-900 tabular-nums leading-none">
                    {seasonAverage(selectedPlayer).display}
                  </div>
                  <div className="text-xs text-secondary-600 mt-1">{seasonAverage(selectedPlayer).label}</div>
                </div>
                <div className="bg-secondary-50 rounded-none p-4 text-center">
                  <div className="text-2xl font-bold text-secondary-900">
                    {selectedPlayer.position === 'K' ? 'Kicker' : 'Punter'}
                  </div>
                  <div className="text-xs text-secondary-600 mt-1">Position</div>
                </div>
              </div>

              {pickError && (
                <p className="text-sm text-danger-600 mt-3">{pickError}</p>
              )}
            </div>

            {/* Modal footer */}
            <div className="flex space-x-3 px-6 pb-6">
              <button
                onClick={() => { setSelectedPlayer(null); setPickError(null); }}
                className="flex-1 btn-outline-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDraftPlayer(selectedPlayer.id)}
                disabled={!isMyTurn || isDrafting || draftState.is_complete}
                className={`flex-1 flex items-center justify-center ${
                  isMyTurn && !isDrafting && !draftState.is_complete
                    ? 'btn-primary'
                    : 'btn bg-secondary-300 text-secondary-500 cursor-not-allowed'
                }`}
              >
                {isDrafting ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2" />
                    Drafting...
                  </>
                ) : !isMyTurn ? (
                  'Not your turn'
                ) : (
                  'Draft Player'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
